import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { ICoursesContract } from "../model";
import { ICourseTag, ICourseWithProgress } from "@features/shared/entities/courses";
import { createEffect, createEvent, createStore, sample } from "effector";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { IRouterContract } from "@features/shared/router";
import { homeTrackClicked, homeTrackCourseClicked } from "@features/shared/events";

const currentCourseTagChanged = createEvent<ICourseTag>();
// Programmatic track switching (the app-tour demo) — no home_track_clicked,
// that event must mean a real user tap.
const currentCourseTagChangedSilently = createEvent<ICourseTag>();
const courseCardClicked = createEvent<{ course: ICourseWithProgress; index: number }>();

const $coursesByTag = createStore<ICourseWithProgress[] | null>(null);

const CAREERS_TAG: ICourseTag = {
    label: "Careers",
    value: "track_careers",
    description: "Learn AI for your profession",
    image: "/images/icons/home/track_careers.png"
};

const BASE_COURSE_TAGS: ICourseTag[] = [
    {
        label: "Images",
        value: "track_images",
        description: "Create stunning visuals with AI",
        image: "/images/icons/home/track_images.png"
    },
    {
        label: "Video",
        value: "track_videos",
        description: "Create videos using AI tools",
        image: "/images/icons/home/track_videos.png"
    },
    {
        label: "Agents",
        value: "track_agents",
        description: "Automate tasks with AI-powered agents",
        image: "/images/icons/home/track_agents.png"
    },
    {
        label: "Text",
        value: "track_text",
        description: "Write better and faster with AI",
        image: "/images/icons/home/track_text.png"
    }
];

const CHALLENGES_TAG: ICourseTag = {
    label: "Challenges",
    value: "challenge",
    description: "Practice with challenge-based courses",
    image: "/images/icons/home/track_challenges.png"
};

const $coursesTags = createStore<ICourseTag[]>([CAREERS_TAG, CHALLENGES_TAG, ...BASE_COURSE_TAGS]);
const $currentCourseTag = createStore<ICourseTag>(CAREERS_TAG);

const trackTrackClickedFx = createEffect((trackName: string) => {
    homeTrackClicked(trackName);
});

const trackCourseClickedFx = createEffect(
    ({ orderNumber, trackName, numberInTrack }: { orderNumber: number; trackName: string; numberInTrack: number }) => {
        homeTrackCourseClicked({ orderNumber, trackName, numberInTrack });
    }
);

sample({
    clock: [currentCourseTagChanged, currentCourseTagChangedSilently],
    source: { coursesTags: $coursesTags },
    fn: (s, p) => {
        const foundTag = s.coursesTags.find((courseTag) => courseTag.value === p.value);
        return foundTag ? foundTag : p;
    },
    target: $currentCourseTag
});

sample({
    clock: courseCardClicked,
    source: { tag: $currentCourseTag },
    fn: (s, p) => ({
        orderNumber: p.course.orderNumber,
        trackName: s.tag.label,
        numberInTrack: p.index + 1
    }),
    target: trackCourseClickedFx
});

sample({
    clock: currentCourseTagChanged,
    fn: (s) => s.label,
    target: trackTrackClickedFx
});

const contract = new ModelContract(
    {
        $coursesByTag,
        $currentCourseTag,
        $coursesTags
    },
    { currentCourseTagChanged, currentCourseTagChangedSilently, courseCardClicked },
    {},
    {}
);

export type IFilteredCoursesContract = typeof contract;

export class FilteredCoursesModule extends ModuleDescription<IFilteredCoursesContract> {
    constructor(
        public coursesModule: ICoursesContract,
        public enrollmentsModule: IEnrollmentsContract,
        public routerModule: IRouterContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            source: {
                courses: this.coursesModule.stores.$courses,
                currentCourseTag: $currentCourseTag,
                enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses
            },
            filter: (s) => s.currentCourseTag !== null && s.courses.length > 0,
            fn: (s) =>
                s.courses
                    .filter((course) => {
                        if (s.currentCourseTag.value === "challenge") {
                            const hasChallengeTag = course.tags?.includes("challenge");
                            return course.type === "challenge" || hasChallengeTag === true;
                        }

                        return course.tags?.includes(s.currentCourseTag.value) && course.type === "course";
                    })
                    .map((course) => {
                        const enrollment = s.enrollmentsCourses.find(
                            (enrollmentCourse) => enrollmentCourse.course_id === course.id
                        );

                        return {
                            ...course,
                            progress: enrollment ? enrollment.progress : null,
                            enrollmentUpdatedAt: enrollment ? enrollment.updated_at : null
                        };
                    }),
            target: $coursesByTag
        });

        sample({
            clock: courseCardClicked,
            fn: (s) => {
                if (s.course.type === "course" || s.course.tags?.includes("challenge")) {
                    return `/course/${s.course.id}`;
                } else {
                    return `/challenge/${s.course.id}`;
                }
            },
            target: this.routerModule.events.routerPush
        });
    }
}
